import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../utils/adminUtils';
import InvoiceSendPanel from './InvoiceSendPanel';
import InvoiceDocumentsList from './InvoiceDocumentsList';
import {
  getPreinvoiceRecipients,
  PREINVOICE_DEFAULT_MESSAGE_TEMPLATE,
  PREINVOICE_TEMPLATE_PLACEHOLDERS,
  SELECTABLE_PAYMENT_STATUSES,
  previewPreinvoiceMessage,
  sendPreinvoices
} from '../utils/preinvoiceUtils';
import {
  INVOICE_DEFAULT_MESSAGE_TEMPLATE,
  INVOICE_DEFAULT_SERVICE,
  INVOICE_TEMPLATE_PLACEHOLDERS,
  getInvoiceRecipients,
  previewInvoiceMessage,
  sendInvoices
} from '../utils/invoiceUtils';

const INVOICE_TEST_ROUTES = [
  {
    title: 'Išankstinė sąskaita faktūra',
    description: 'Patikrai su testiniais pirkėjo duomenimis ir spausdinimo/PDF mygtuku.',
    to: '/admin/invoices/test-proforma',
    buttonLabel: 'Atidaryti testą',
  },
  {
    title: 'Sąskaita faktūra',
    description: 'Standartinė sąskaitos faktūros testinė forma su paslaugų eilutėmis.',
    to: '/admin/invoices/test-factura',
    buttonLabel: 'Atidaryti testą',
  },
  {
    title: 'Sąskaita faktūra (MB pirkėjas)',
    description: 'Alternatyvus testas MB pirkėjui be logotipo.',
    to: '/admin/invoices/test-coach-mb',
    buttonLabel: 'Atidaryti testą',
  },
];

export default function AdminSaskaitosPage() {
  const { currentUser } = useAuth();
  const isAdminUser = isUserAdmin(currentUser);
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  if (!currentUser || !isAdminUser) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Tik administratorius gali pasiekti sąskaitų generavimą.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="md">
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Sąskaitos
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Šiame puslapyje galite siųsti išankstines sąskaitas ir sąskaitas faktūras klientams.
        </Typography>

        <InvoiceSendPanel
          title="Išankstinė sąskaita klientams"
          description="Parinkite gavėjus ir sumą. Kiekvienam gavėjui sugeneruojamas PDF, išsaugomas Firebase Storage bei įrašomas į preinvoices kolekciją."
          sendButtonLabel="Siųsti išankstinę sąskaitą-faktūrą"
          selectionDialogTitle="Siųsti išankstinę sąskaitą-faktūrą"
          messageDialogTitle="El. laiško žinutės peržiūra ir redagavimas"
          defaultMessageTemplate={PREINVOICE_DEFAULT_MESSAGE_TEMPLATE}
          templatePlaceholders={PREINVOICE_TEMPLATE_PLACEHOLDERS}
          getRecipients={getPreinvoiceRecipients}
          sendDocuments={sendPreinvoices}
          previewMessage={previewPreinvoiceMessage}
          defaultPaymentStatus="pending payment"
          paymentStatusOptions={SELECTABLE_PAYMENT_STATUSES}
        />

        <InvoiceSendPanel
          title="Sąskaita faktūra klientams"
          description="Parinkite gavėjus ir sumą. Kiekvienam gavėjui sugeneruojama sąskaita faktūra su paslaugų eilutėmis, išsaugoma Firebase Storage bei įrašoma į invoices kolekciją."
          sendButtonLabel="Siųsti sąskaitą faktūrą"
          selectionDialogTitle="Siųsti sąskaitą faktūrą"
          messageDialogTitle="El. laiško žinutės peržiūra ir redagavimas"
          defaultMessageTemplate={INVOICE_DEFAULT_MESSAGE_TEMPLATE}
          templatePlaceholders={INVOICE_TEMPLATE_PLACEHOLDERS}
          getRecipients={getInvoiceRecipients}
          sendDocuments={sendInvoices}
          previewMessage={previewInvoiceMessage}
          showServiceField
          defaultService={INVOICE_DEFAULT_SERVICE}
          showServicePeriodField
          showDeliveryModeOption
          defaultPaymentStatus="Payed"
          paymentStatusOptions={SELECTABLE_PAYMENT_STATUSES}
        />

        <InvoiceDocumentsList enabled={isAdminUser} currentYear={currentYear} />

        <Stack spacing={2.5} sx={{ mt: 2.5 }}>
          {INVOICE_TEST_ROUTES.map((route) => (
            <Paper key={route.to} sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {route.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {route.description}
              </Typography>
              <Button component={RouterLink} to={route.to} variant="outlined">
                {route.buttonLabel}
              </Button>
            </Paper>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
